import React, { useEffect, useState } from 'react';
import { CocoEvent } from '@cotera/client/app/etc';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { BroadcastEventType } from '@cotera/api';
import { emit } from '@cotera/client/app/etc/broadcast-event-bus';
import {
  ActionResult,
  ActionTrackMetadata,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import { ModalPageView } from '@cotera/client/app/components/app';
import { Loading, toast } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ResourceSelectionModel,
  ResourceType,
} from '../resources/selection/resource-selection.model';
import { DatasetModel } from './dataset.model';

const renameDatasetParamsSchema = z.object({
  name: z.string().min(1),
  // A row kebab menu already knows which dataset it targets, so it can pass the
  // id directly rather than relying on the shared selection model.
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof renameDatasetParamsSchema>;

type ModalManager = {
  push: (props: { view: () => React.ReactNode; onClose?: () => void }) => void;
  pop: () => void;
};

type RenameDatasetActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'updateSettings'>;
  };
};

export class RenameDatasetAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Rename Dataset';
  description = 'Rename a dataset';
  inputSchema = renameDatasetParamsSchema;
  group = 'Dataset';
  icon = 'database';

  private _lastRenamedDatasetId: string | null = null;

  constructor(
    private readonly client: RenameDatasetActionClient,
    private readonly modalManager: ModalManager,
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  getPriority(context: ApplicableContext): number {
    return this.getTargetDatasetId(context) !== null ? 20 : 0;
  }

  shouldAsk(): boolean {
    return true;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    // Naming a dataset is worth keeping, so it creates one that has not been
    // persisted yet rather than failing against an id the server has never
    // seen.
    const datasetModel = context.targetedModelOfType(DatasetModel);
    if (datasetModel !== null && !datasetModel.isPersisted) {
      const persisted = await datasetModel.persist();
      if (persisted.isErr()) {
        toast.error(persisted.error);
        return err([{ t: 'error', message: persisted.error }]);
      }
    }

    // Renaming goes through the same settings endpoint as the saved preview
    // query; omitted fields are left untouched.
    const result = await this.client.resource.dataset.updateSettings({
      id: datasetId,
      name: payload.name.trim(),
    });

    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to rename dataset';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }

    // Restream the resource lists and the dataset page itself so the new name
    // shows without a manual refetch.
    emit(BroadcastEventType.ResourceUpdated, {
      orgId: this.orgId,
      resourceType: 'dataset',
      resourceId: datasetId,
    });

    const selection = context.targetedModelOfType(ResourceSelectionModel);
    if (selection !== null) {
      context.markAsNotTargeted(selection);
    }

    this._lastRenamedDatasetId = datasetId;

    toast.success('Dataset renamed');
    return ok({});
  }

  track(): ActionTrackMetadata | null {
    if (this._lastRenamedDatasetId === null) {
      return null;
    }
    return {
      event: CocoEvent.DatasetRenamed,
      properties: { datasetId: this._lastRenamedDatasetId },
    };
  }

  ask(
    params: Partial<Params>,
    context: ExecuteContext
  ): AskResponse<Params, { t: string; message?: string }[]> {
    const datasetId = params.datasetId ?? this.getTargetDatasetId(context);
    const initialName = params.name ?? this.getInitialNameFromContext(context);
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async () => {
          await onCancel();
          this.modalManager.pop();
        };

        this.modalManager.push({
          view: () => (
            <RenameDatasetAskView
              datasetId={datasetId}
              initialName={initialName}
              onRespond={async (nextParams) => {
                await onRespond(nextParams);
                this.modalManager.pop();
              }}
              onCancel={handleCancel}
            />
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  private selectedDatasets(
    context: ApplicableContext
  ): ResourceSelectionModel | null {
    if (!context.isInScope(ResourceSelectionModel)) {
      return null;
    }
    const selection = context.getInScopeModelsOfType(ResourceSelectionModel);
    const single = selection.length === 1 ? selection[0] : undefined;
    if (
      single === undefined ||
      single.selections.length !== 1 ||
      single.getResourceIdsForType(ResourceType.Dataset).length !== 1
    ) {
      return null;
    }
    return single;
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const selected = this.selectedDatasets(context);
    if (selected !== null) {
      return selected.getResourceIdsForType(ResourceType.Dataset)[0] ?? null;
    }

    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }

  private getInitialNameFromContext(
    context: ExecuteContext
  ): string | undefined {
    const selected = this.selectedDatasets(context);
    if (selected !== null) {
      return selected.getResourceForType(ResourceType.Dataset)?.name;
    }

    const datasetModel = context.targetedModelOfType(DatasetModel);
    return (
      datasetModel?.datasetDataWatchable.snapshot()?.resource?.dataset.name ??
      undefined
    );
  }
}

const RenameDatasetAskView: React.FC<{
  datasetId: string | null;
  initialName?: string;
  onRespond: (params: Params) => Promise<void>;
  onCancel: () => Promise<void>;
}> = ({ datasetId, initialName, onRespond, onCancel }) => {
  const [name, setName] = useState(initialName ?? '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialName !== undefined) {
      setName(initialName);
    }
  }, [initialName]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0 || loading || datasetId === null) {
      return;
    }
    setLoading(true);
    await onRespond({ name: trimmed });
    setLoading(false);
  };

  return (
    <ModalPageView
      title="Rename dataset"
      subtitle="Choose a new name for this dataset"
      className="px-4 py-4"
    >
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label htmlFor="dataset-name" className="text-sm font-medium">
            Dataset name
          </label>
          <Input
            id="dataset-name"
            autoFocus
            placeholder="Dataset name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void onCancel()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || datasetId === null || name.trim().length === 0}
          >
            {loading ? <Loading.Spinner variant="sm" /> : 'Rename'}
          </Button>
        </div>
      </form>
    </ModalPageView>
  );
};
